/**
 * The running build's version, and the deployed `version.json` it is compared against.
 *
 * Every asset is cached forever (see `release/index.ts`), so an open tab keeps running the build it
 * loaded until it reloads. This module is how it finds out it is behind. Both values are baked in by
 * the build; a build without them (local dev, the S3 flow) is version 0, and 0 turns the check off.
 */

export type TAppVersionStatus = 'disabled' | 'current' | 'update_available' | 'update_required';

export interface IVersionManifest {
  version: number;
  minVersion: number;
}

const toVersion = (raw: unknown): number => {
  const value = Number(raw);
  return Number.isInteger(value) && value >= 0 ? value : 0;
};

/** The version this tab is running. */
export const getBuildVersion = (): number => toVersion(import.meta.env.AP_APP_BUILD_VERSION);

let localeHashes: Record<string, string> | undefined;

/** Content hash of a locale file, or undefined when the build did not record one. */
export const getLocaleHash = (language: string, namespace: string): string | undefined => {
  if (localeHashes === undefined) {
    try {
      const raw = import.meta.env.AP_APP_LOCALE_HASHES;
      localeHashes = typeof raw === 'string' && raw ? JSON.parse(raw) : {};
    } catch {
      localeHashes = {};
    }
  }
  return localeHashes?.[`${language}/${namespace}`];
};

/**
 * Parses a `version.json` body. Returns null for anything malformed, so a bad manifest can never
 * lock a tab.
 */
export const parseVersionManifest = (body: unknown): IVersionManifest | null => {
  if (typeof body !== 'object' || body === null) return null;
  const { version, min_version: minVersion } = body as Record<string, unknown>;
  if (!Number.isInteger(version) || (version as number) < 0) return null;
  const min = minVersion === undefined ? 0 : minVersion;
  if (!Number.isInteger(min) || (min as number) < 0) return null;
  return { version: version as number, minVersion: min as number };
};

/** Fetches the deployed manifest, bypassing every cache. Returns null on any failure. */
export const fetchVersionManifest = async (url: string): Promise<IVersionManifest | null> => {
  try {
    const response = await fetch(url, { cache: 'no-store', credentials: 'omit' });
    if (!response.ok) return null;
    return parseVersionManifest(await response.json());
  } catch {
    return null;
  }
};

/** Where this tab stands against the deployed manifest. */
export const resolveVersionStatus = (
  current: number,
  manifest: IVersionManifest | null
): TAppVersionStatus => {
  if (current === 0) return 'disabled';
  if (manifest === null) return 'current';
  if (manifest.minVersion > current) return 'update_required';
  if (manifest.version > current) return 'update_available';
  return 'current';
};
